import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowLeft, Paperclip, CheckCircle2, RotateCcw } from "lucide-react";
import { STATUS_COL, PRIO_COL } from "@/pages/docs/FeedbackAdmin";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ["new", "under_review", "approved", "in_progress", "ready_for_testing", "resolved", "closed", "rejected"];
const SLBL = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const dstr = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export default function FeedbackTicket() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [assignees, setAssignees] = useState([]);

  const load = () => api.get(`/feedback/${id}`).then((r) => setD(r.data)).catch(() => setD(false));
  useEffect(() => { load(); api.get("/feedback/meta").then((r) => setAssignees(r.data.assignees || [])).catch(() => {}); }, [id]); // eslint-disable-line

  if (d === null) return <div className="flex items-center gap-2 text-slate-500 py-10"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading…</div>;
  if (d === false) return <div className="text-slate-500 py-10">Ticket not found or you don't have access.</div>;
  const t = d.ticket, mgr = d.is_manager, mine = d.is_submitter;

  const patch = async (body) => { setBusy(true); try { await api.patch(`/feedback/${id}`, body); await load(); toast.success("Updated"); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed"); } finally { setBusy(false); } };
  const addNote = async (kind) => {
    if (!note.trim()) return; setBusy(true);
    try { await api.post(`/feedback/${id}/notes`, { text: note, kind }); setNote(""); await load(); toast.success("Added"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
  };
  const verify = async (kind) => { setBusy(true); try { await api.post(`/feedback/${id}/verify`, { kind, text: note }); setNote(""); await load(); toast.success(kind === "confirm" ? "Confirmed resolved" : "Reopened"); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); } finally { setBusy(false); } };

  return (
    <div className="max-w-4xl" data-testid="feedback-ticket">
      <Link to="/docs/feedback" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-4"><ArrowLeft className="w-4 h-4" /> All feedback</Link>

      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-slate-400">{t.ref}</span>
            <span className={`text-[11px] font-semibold rounded-md px-2 py-0.5 ${STATUS_COL[t.status]}`} data-testid="ft-status">{SLBL(t.status)}</span>
            <span className={`text-[11px] font-semibold rounded-md px-2 py-0.5 capitalize ${PRIO_COL[t.priority]}`}>{t.priority}</span>
            <span className="text-[11px] font-semibold rounded-md px-2 py-0.5 bg-slate-100 text-slate-600 capitalize">{t.type}</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="ft-title">{t.title}</h1>
          <p className="text-xs text-slate-500 mt-1">By {t.submitted_by?.name} · {dstr(t.created_at)} · <span className="font-mono">{t.page_url}</span>{t.record_ref ? <> · ref <span className="font-mono">{t.record_ref}</span></> : null}</p>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-sm text-slate-700 whitespace-pre-wrap">{t.description || <span className="text-slate-400">No description.</span>}</p>
        {(t.attachments || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="ft-attachments">
            {t.attachments.map((a) => (
              <a key={a.id} href={`${API}/api/files/${a.path}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-indigo-600 border border-indigo-200 rounded-lg px-2.5 py-1 hover:bg-indigo-50">
                <Paperclip className="w-3.5 h-3.5" /> {a.filename || "screenshot"}
              </a>
            ))}
          </div>
        )}
        {t.resolution && <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800" data-testid="ft-resolution"><b>Resolution:</b> {t.resolution}</div>}
      </div>

      {mgr && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="ft-manage">
          <h3 className="text-sm font-bold text-slate-800">Manage</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-xs text-slate-500">Status
              <select defaultValue={t.status} onChange={(e) => patch({ status: e.target.value })} data-testid="ft-status-select" className="mt-1 w-full h-9 rounded-lg border border-slate-200 px-2 text-sm">
                {STATUSES.map((s) => <option key={s} value={s}>{SLBL(s)}</option>)}
              </select>
            </label>
            <label className="text-xs text-slate-500">Priority
              <select defaultValue={t.priority} onChange={(e) => patch({ priority: e.target.value })} data-testid="ft-priority-select" className="mt-1 w-full h-9 rounded-lg border border-slate-200 px-2 text-sm capitalize">
                {["low", "medium", "high"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="text-xs text-slate-500">Assign to
              <select defaultValue={t.assigned_to_id || ""} onChange={(e) => patch({ assigned_to_id: e.target.value })} data-testid="ft-assign-select" className="mt-1 w-full h-9 rounded-lg border border-slate-200 px-2 text-sm">
                <option value="">Unassigned</option>
                {(assignees || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
            <label className="text-xs text-slate-500">Edit title
              <div className="flex gap-2 mt-1">
                <Input defaultValue={t.title} id="ft-title-input" className="h-9" data-testid="ft-title-input" />
                <Button variant="outline" className="h-9" onClick={() => patch({ title: document.getElementById("ft-title-input").value })}>Save</Button>
              </div>
            </label>
          </div>
          <label className="text-xs text-slate-500 block">Resolution
            <div className="flex gap-2 mt-1">
              <Input defaultValue={t.resolution || ""} id="ft-res-input" className="h-9" data-testid="ft-resolution-input" placeholder="Describe the fix" />
              <Button variant="outline" className="h-9" onClick={() => patch({ resolution: document.getElementById("ft-res-input").value })}>Record</Button>
            </div>
          </label>
        </div>
      )}

      {/* Activity + notes */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-bold text-slate-800 mb-3">Activity</h3>
        <ol className="space-y-2.5" data-testid="ft-timeline">
          {(t.events || []).map((e) => (
            <li key={e.id} className="flex gap-3 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-2 shrink-0" />
              <div>
                <div className="text-slate-700">{e.kind === "note" && <span className="text-[10px] font-bold text-amber-600 mr-1">INTERNAL</span>}{e.text}</div>
                <div className="text-[11px] text-slate-400">{e.actor_name} · {dstr(e.ts)}</div>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-4 border-t border-slate-100 pt-3 space-y-2">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="ft-note-input"
            placeholder={mgr ? "Add a note or comment…" : "Add a comment or reply…"}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          <div className="flex flex-wrap gap-2">
            <Button className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={() => addNote("comment")} data-testid="ft-comment-btn">Comment</Button>
            {mgr && <Button variant="outline" className="h-9" disabled={busy} onClick={() => addNote("note")} data-testid="ft-note-btn">Internal note</Button>}
            {mgr && <Button variant="outline" className="h-9" disabled={busy} onClick={() => addNote("request_info")} data-testid="ft-requestinfo-btn">Request info</Button>}
            {mine && t.status === "ready_for_testing" && (
              <>
                <Button className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" disabled={busy} onClick={() => verify("confirm")} data-testid="ft-confirm-btn"><CheckCircle2 className="w-4 h-4" /> Confirm fixed</Button>
                <Button variant="outline" className="h-9 gap-1.5 text-rose-600 border-rose-200" disabled={busy} onClick={() => verify("reopen")} data-testid="ft-reopen-btn"><RotateCcw className="w-4 h-4" /> Still broken</Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
